import { AlertTriangle } from 'lucide-react';
import { Modal } from '../../../../components/common/Modal';
import { useLanguage } from '../../../../features/language/useLanguage';
import type { VoucherRow } from '../../../../services/subscriptionService';

interface DeleteVoucherDialogProps {
  voucher: VoucherRow;
  onCancel: () => void;
  onConfirm: () => void;
  isPending: boolean;
}

/**
 * Confirmation dialog for deleting a voucher. Kept as a dedicated component
 * (rather than the shared ConfirmationDialog) because it stays open showing a
 * pending state while the delete mutation runs.
 */
export function DeleteVoucherDialog({ voucher, onCancel, onConfirm, isPending }: DeleteVoucherDialogProps) {
  const { t } = useLanguage();
  return (
    <Modal isOpen onClose={onCancel} title={t('subscription.voucher.delete_title')} maxWidth="sm">
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <p className="text-sm text-gray-600 pt-1">
            {t('subscription.voucher.delete_description', { code: voucher.code })}
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
          <button
            onClick={onCancel}
            disabled={isPending}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm font-medium shadow-sm disabled:opacity-50 transition-colors"
            data-test-id="voucher-delete-confirm"
          >
            {isPending ? t('common.loading') : t('common.delete')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
